import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { style } from './zui-checkbox-css.js';
import { classMap } from 'lit/directives/class-map.js';
import type { PropertyValues } from 'lit';

/**
 * Checkbox form control.
 * @element zui-checkbox
 *
 * @event change - When the checked state changes, an event is dispatched with the `checked` boolean details
 *
 * @slot - Default, unnamed slot; for inserting label text, if applicable, into `<zui-checkbox>`
 * @slot icon - Used to create "gallery" checkboxes
 *
 * @attr {string | null} [name=null] - The name of this element that is associated with form submission
 * @attr {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 *
 * @prop {string | null} [name=null] - The name of this element that is associated with form submission
 * @prop {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 *
 * @cssprop [--zui-checkbox-color=var(--zui-blue)] - (default / `gallery`): checkbox background color
 * @cssprop [--zui-checkbox-animate-color=var(--zui-blue-200)] - (default): checkbox background color while animating, on click triggers animation
 * @cssprop [--zui-checkbox-checkbox-margin=0.6875rem 0.6875rem 0.6875rem 0 (11px 11px 11px 0)] - (default): checkbox marginal positioning
 * @cssprop [--zui-checkbox-label-margin=0.5625rem 1.25rem 0.5625rem 0 (9px 20px 9px 0)] - (default): checkbox label's marginal positioning
 * @cssprop [--zui-checkbox-gallery-background=var(--zui-blue-100)] - (`gallery`): background color
 */
export class ZuiCheckbox extends ZuiFormAssociatedElement {
  protected get _formValue() {
    return this.checked ? this.value : null;
  }

  protected get _focusControlSelector() {
    return '.checkbox';
  }

  #initialState = false;

  /**
   * Whether or not the checkbox is checked
   */
  @property({ type: Boolean, reflect: true })
  checked = false;

  /**
   * The value of the checkbox; included in form submissions when the checkbox element is checked.
   */
  @property({ type: String, reflect: true })
  value = 'on';

  /**
   * If set, the checkbox's value is neither `true` nor `false`, but instead in a state that cannot be determined. This can be useful with "sub-checkboxes", where the checked state of the parent checkbox is determined by the states of all child checkboxes.
   */
  @property({ type: Boolean, reflect: true })
  indeterminate = false;

  #instantiated = false;
  #checkedChanged = false;

  protected formResetCallback() {
    this.checked = this.#initialState;
    this._setFormValue(this._formValue);
  }

  connectedCallback() {
    super.connectedCallback();

    // we want to go a little faster than LitElement and behave more like native HTML Form Associated Elements
    const initialChecked = this.checked || this.hasAttribute('checked');
    const initialValue = this.value || this.getAttribute('value');
    this.#initialState = initialChecked;
    this._setFormValue(initialChecked ? initialValue : null);

    this.setAttribute('role', 'checkbox');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);

    this.#instantiated = true;
    this.requestUpdate();
  }

  update(changedProps: PropertyValues) {
    super.update(changedProps);

    if (changedProps.has('value')) {
      this._setFormValue(this._formValue);
    }

    if (changedProps.has('checked') && this.#instantiated) {
      this.#checkedChanged = true;
      this.requestUpdate();
    }
  }

  #onKeydown(e: KeyboardEvent) {
    if (!this.disabled && (e.key === 'Enter' || e.key === ' ')) {
      this.#toggleChecked();
      e.preventDefault();
      e.stopPropagation();
    }
  }

  #onClick(e: MouseEvent) {
    if (!this.disabled) {
      this.#toggleChecked();
    }
    e.preventDefault();
    e.stopPropagation();
  }

  // What about readonly? It's super weird...
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#readonly
  #toggleChecked() {
    this.checked = !this.checked;
    this.indeterminate = false;
    this._setFormValue(this._formValue);
    this.dispatchEvent(new CustomEvent('change', { detail: { checked: this.checked }, bubbles: true }));
  }

  #setOrRemoveCheckedAttribute() {
    this.checked ? this.setAttribute('aria-checked', 'true') : this.setAttribute('aria-checked', 'false');
  }

  #setOrRemoveDisabledAttribute() {
    this.disabled ? this.setAttribute('aria-disabled', 'true') : this.setAttribute('aria-disabled', 'false');
  }

  static get styles() {
    return [super.styles, style];
  }

  render() {
    this.#setOrRemoveCheckedAttribute();
    this.#setOrRemoveDisabledAttribute();

    const tabIndex = this.disabled ? '-1' : '0';

    return html`
      <section class="checkbox-container" @click="${this.#onClick}" @keydown="${this.#onKeydown}">
        <div class="checkbox ${classMap({ 'checked-changed': this.#checkedChanged })}" tabindex="${tabIndex}">
          ${this.indeterminate
            ? html` <div class="indeterminate-container"> <div class="indeterminate"> </div> </div> `
            : html` <div class="checkmark-container"> <div class="checkmark"> </div> </div> `}
          <div class="animate"></div>
        </div>
        <div class="label">
          <div class="icon"><slot name="icon"></slot></div> <div class="label-text"><slot></slot></div>
        </div>
      </section>
    `;
  }
}

window.customElements.define('zui-checkbox', ZuiCheckbox);

declare global {
  interface HTMLElementTagNameMap {
    'zui-checkbox': ZuiCheckbox;
  }
}
